import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Grid,
} from '@mui/material';
import { ArrowBack, AddCircle, Delete, Save } from '@mui/icons-material';
import { PageHeader, headerCellSx } from '../../components/common/pageKit';
import { api } from '../../services/apiClient';
import { procurementService, Vendor, RfqType } from '../../services/procurementService';
import { projectService, User } from '../../services/projectService';

interface Warehouse {
  id: number | string;
  warehouseCode?: string;
  warehouseName: string;
}

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP'];

// ---------------------------------------------------------------------------
// Create / edit an RFQ. Mirrors PurchaseRequisitionFormPage.tsx's structure:
// header fields, a dynamic item-rows table, plus (RFQ-specific) a
// multi-select of vendors to invite. Editing is only ever legal while the
// RFQ is DRAFT, same rule the backend enforces server-side.
// ---------------------------------------------------------------------------

interface Product {
  id: string;
  product_name: string;
  hsn_code: string;
  unit: string;
  is_active?: boolean;
}

const RFQ_TYPES: RfqType[] = ['MATERIAL', 'SERVICE', 'CAPEX', 'SUBCONTRACTING', 'JOB_WORK', 'CONSUMABLE', 'ASSET', 'OTHER'];

let draftKeySeq = 0;
const nextDraftKey = () => `draft-${++draftKeySeq}`;

interface ItemDraft {
  key: string;
  product: Product | null;
  itemDescription: string;
  quantity: string;
  uom: string;
  specification: string;
  requiredDate: string;
}

const emptyItem = (): ItemDraft => ({
  key: nextDraftKey(),
  product: null,
  itemDescription: '',
  quantity: '1',
  uom: '',
  specification: '',
  requiredDate: '',
});

const toNum = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

interface RfqItemFromApi {
  id: number;
  product_id: string | null;
  item_description: string | null;
  hsn_code: string | null;
  quantity: number | string;
  uom: string | null;
  required_date: string | null;
  specification: string | null;
}

interface RfqFromApi {
  id: number;
  rfq_type: RfqType;
  rfq_date: string;
  response_deadline: string | null;
  delivery_location: string | null;
  payment_terms: string | null;
  incoterms: string | null;
  notes: string | null;
  status: string;
  warehouse_id: number | string | null;
  currency: string | null;
  buyer_id: number | string | null;
  terms_conditions: string | null;
  items: RfqItemFromApi[];
  suppliers: { vendor_id: string }[];
}

const RfqFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [products, setProducts] = useState<Product[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [buyers, setBuyers] = useState<User[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [loadingRfq, setLoadingRfq] = useState(isEdit);

  const [rfqType, setRfqType] = useState<RfqType>('MATERIAL');
  const [rfqDate, setRfqDate] = useState(todayISO());
  const [responseDeadline, setResponseDeadline] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [incoterms, setIncoterms] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [buyerId, setBuyerId] = useState('');
  const [termsConditions, setTermsConditions] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([emptyItem()]);
  const [selectedVendors, setSelectedVendors] = useState<Vendor[]>([]);
  const [status, setStatus] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    setLoadingRefData(true);
    Promise.all([
      api.get<{ products: Product[] }>('/products').catch(() => ({ products: [] as Product[] })),
      procurementService.getVendors().catch(() => ({ vendors: [] as Vendor[], count: 0 })),
      api.get<{ warehouses: Warehouse[] }>('/inventory/warehouses').catch(() => ({ warehouses: [] as Warehouse[] })),
      projectService.getUsers().catch(() => [] as User[]),
    ])
      .then(([productsRes, vendorsRes, warehousesRes, usersRes]) => {
        setProducts((productsRes.products || []).filter((p) => p.is_active !== false));
        setVendors((vendorsRes.vendors || []).filter((v) => v.is_active !== false));
        setWarehouses(warehousesRes.warehouses || []);
        setBuyers(usersRes || []);
      })
      .finally(() => setLoadingRefData(false));
  }, []);

  useEffect(() => {
    if (!isEdit || !id || loadingRefData) return;
    setLoadingRfq(true);
    api
      .get<{ rfq: RfqFromApi }>(`/rfqs/${id}`)
      .then(({ rfq }) => {
        setRfqType(rfq.rfq_type);
        setRfqDate(rfq.rfq_date ? rfq.rfq_date.slice(0, 10) : todayISO());
        setResponseDeadline(rfq.response_deadline ? rfq.response_deadline.slice(0, 10) : '');
        setDeliveryLocation(rfq.delivery_location || '');
        setPaymentTerms(rfq.payment_terms || '');
        setIncoterms(rfq.incoterms || '');
        setWarehouseId(rfq.warehouse_id ? String(rfq.warehouse_id) : '');
        setCurrency(rfq.currency || 'INR');
        setBuyerId(rfq.buyer_id ? String(rfq.buyer_id) : '');
        setTermsConditions(rfq.terms_conditions || '');
        setNotes(rfq.notes || '');
        setStatus(rfq.status);
        setItems(
          (rfq.items || []).map((it) => ({
            key: nextDraftKey(),
            product: products.find((p) => String(p.id) === String(it.product_id)) || null,
            itemDescription: it.item_description || '',
            quantity: String(it.quantity ?? ''),
            uom: it.uom || '',
            specification: it.specification || '',
            requiredDate: it.required_date ? it.required_date.slice(0, 10) : '',
          }))
        );
        const invitedIds = new Set((rfq.suppliers || []).map((s) => String(s.vendor_id)));
        setSelectedVendors(vendors.filter((v) => invitedIds.has(String(v.id))));
      })
      .catch((err: any) => setError(err.message || 'Failed to load RFQ'))
      .finally(() => setLoadingRfq(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, loadingRefData]);

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (key: string) => setItems((prev) => prev.filter((it) => it.key !== key));
  const updateItem = (key: string, patch: Partial<ItemDraft>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  const handleProductChange = (key: string, product: Product | null) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.key !== key) return it;
        return {
          ...it,
          product,
          uom: it.uom || product?.unit || '',
          itemDescription: it.itemDescription || product?.product_name || '',
        };
      })
    );
  };

  const canEdit = !isEdit || status === 'DRAFT';

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!rfqDate) errs.push('RFQ date is required.');
    if (items.length === 0) errs.push('At least one line item is required.');
    items.forEach((it, idx) => {
      if (toNum(it.quantity) <= 0) errs.push(`Line ${idx + 1}: quantity must be greater than 0.`);
    });
    if (!isEdit && selectedVendors.length === 0) {
      errs.push('Invite at least one supplier so this RFQ can be issued.');
    }
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload: Record<string, any> = {
        rfqType,
        rfqDate,
        responseDeadline: responseDeadline || undefined,
        deliveryLocation: deliveryLocation.trim() || undefined,
        paymentTerms: paymentTerms.trim() || undefined,
        incoterms: incoterms.trim() || undefined,
        warehouseId: warehouseId ? Number(warehouseId) : undefined,
        currency: currency || 'INR',
        buyerId: buyerId ? Number(buyerId) : undefined,
        termsConditions: termsConditions.trim() || undefined,
        notes: notes.trim() || undefined,
        items: items.map((it) => ({
          productId: it.product?.id || undefined,
          itemDescription: it.itemDescription.trim() || it.product?.product_name || undefined,
          hsnCode: it.product?.hsn_code || undefined,
          quantity: toNum(it.quantity),
          uom: it.uom.trim() || undefined,
          requiredDate: it.requiredDate || undefined,
          specification: it.specification.trim() || undefined,
        })),
      };
      if (!isEdit) {
        payload.vendorIds = selectedVendors.map((v) => v.id);
      }

      const response = isEdit && id
        ? await procurementService.updateRfq(id, payload)
        : await procurementService.createRfq(payload);

      navigate(`/procurement/rfqs/${response.rfq.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to save RFQ');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = loadingRefData || loadingRfq;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(isEdit && id ? `/procurement/rfqs/${id}` : '/procurement/rfqs')}
        sx={{ mb: 1 }}
      >
        Back
      </Button>

      <PageHeader
        title={isEdit ? 'Edit RFQ' : 'New RFQ'}
        subtitle="Describe what you need quoted, and invite the suppliers who should bid on it."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!canEdit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This RFQ is {status} and can no longer be edited.
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((e) => (
            <Box key={e}>{e}</Box>
          ))}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          RFQ Details
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="RFQ Type"
              value={rfqType}
              onChange={(e) => setRfqType(e.target.value as RfqType)}
              disabled={!canEdit}
            >
              {RFQ_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="RFQ Date"
              value={rfqDate}
              onChange={(e) => setRfqDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Response Deadline"
              value={responseDeadline}
              onChange={(e) => setResponseDeadline(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Delivery Location"
              value={deliveryLocation}
              onChange={(e) => setDeliveryLocation(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Payment Terms"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g. Net 30"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Incoterms"
              value={incoterms}
              onChange={(e) => setIncoterms(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g. FOB, EXW"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Warehouse"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              disabled={!canEdit}
            >
              <MenuItem value="">Not assigned</MenuItem>
              {warehouses.length === 0 ? (
                <MenuItem value="" disabled>
                  No warehouses configured
                </MenuItem>
              ) : (
                warehouses.map((w) => (
                  <MenuItem key={w.id} value={String(w.id)}>
                    {w.warehouseCode ? `${w.warehouseCode} -- ${w.warehouseName}` : w.warehouseName}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              disabled={!canEdit}
            >
              {CURRENCIES.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Buyer"
              value={buyerId}
              onChange={(e) => setBuyerId(e.target.value)}
              disabled={!canEdit}
            >
              <MenuItem value="">Not assigned</MenuItem>
              {buyers.length === 0 ? (
                <MenuItem value="" disabled>
                  No buyers available
                </MenuItem>
              ) : (
                buyers.map((u) => (
                  <MenuItem key={u.id} value={String(u.id)}>
                    {u.full_name}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              multiple
              size="small"
              options={vendors}
              value={selectedVendors}
              onChange={(_, v) => setSelectedVendors(v)}
              getOptionLabel={(o) => `${o.company_name} (${o.vendor_code})`}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              disabled={!canEdit || isEdit}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option.id} size="small" label={option.company_name} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Invite Suppliers"
                  placeholder={selectedVendors.length ? '' : 'Select vendors to invite'}
                  helperText={isEdit ? 'Use Invite Suppliers on the RFQ detail page to add more.' : undefined}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!canEdit}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Terms & Conditions"
              value={termsConditions}
              onChange={(e) => setTermsConditions(e.target.value)}
              disabled={!canEdit}
              placeholder="Terms suppliers must accept when responding"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 2.5 }}>
        <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Line Items
          </Typography>
          {canEdit && (
            <Button size="small" variant="outlined" startIcon={<AddCircle />} onClick={addItem}>
              Add Item
            </Button>
          )}
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Product</TableCell>
                <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                <TableCell sx={headerCellSx}>UOM</TableCell>
                <TableCell sx={headerCellSx}>Required By</TableCell>
                <TableCell sx={headerCellSx}>Specification</TableCell>
                {canEdit && <TableCell sx={headerCellSx} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.key} hover>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Autocomplete
                      size="small"
                      options={products}
                      value={it.product}
                      onChange={(_, v) => handleProductChange(it.key, v)}
                      getOptionLabel={(o) => `${o.product_name}${o.hsn_code ? ` (${o.hsn_code})` : ''}`}
                      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                      disabled={!canEdit}
                      renderInput={(params) => <TextField {...params} placeholder="Select product" />}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 90 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={it.quantity}
                      onChange={(e) => updateItem(it.key, { quantity: e.target.value })}
                      inputProps={{ min: 0, step: '0.01' }}
                      disabled={!canEdit}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 90 }}>
                    <TextField
                      size="small"
                      value={it.uom}
                      onChange={(e) => updateItem(it.key, { uom: e.target.value })}
                      disabled={!canEdit}
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <TextField
                      size="small"
                      type="date"
                      value={it.requiredDate}
                      onChange={(e) => updateItem(it.key, { requiredDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={!canEdit}
                      sx={{ minWidth: 140 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <TextField
                      size="small"
                      value={it.specification}
                      onChange={(e) => updateItem(it.key, { specification: e.target.value })}
                      disabled={!canEdit}
                      sx={{ minWidth: 170 }}
                    />
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => removeItem(it.key)} disabled={items.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={() => navigate(isEdit && id ? `/procurement/rfqs/${id}` : '/procurement/rfqs')}>Cancel</Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create RFQ'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default RfqFormPage;
